<template>
  <div class="vehicles-map-shell">
    <div
      id="map"
      class="vehicles-map"
    />
    <!-- Route-planner toggle lives in MapView's mapv3-controls cluster (bottom-right); this shell
         only renders the summary panel while the planner is active. -->
    <div
      v-if="routePlanner.active"
      class="route-planner-panel"
    >
      <template v-if="routePlanner.loading">
        <span class="fa fa-circle-notch fa-spin" /> {{ $t('components.map.routePlanning.loading') }}
      </template>
      <template v-else-if="routePlanner.summary">
        <div class="route-planner-panel__row">
          <i class="bi bi-arrows-expand-vertical" /> {{ $t('components.map.routePlanning.distance') }}:
          <strong>{{ routePlanner.summary.distance }} km</strong>
        </div>
        <div class="route-planner-panel__row">
          <i class="bi bi-clock" /> {{ $t('components.map.routePlanning.duration') }}:
          <strong>{{ formatRouteDuration(routePlanner.summary.duration) }}</strong>
        </div>
        <button
          class="route-planner-panel__clear"
          @click="clearRoutePlan"
        >
          {{ $t('components.map.routePlanning.clear') }}
        </button>
      </template>
      <template v-else>
        {{ selectedVehicleImei
          ? $t('components.map.routePlanning.clickDestination')
          : $t('components.map.routePlanning.selectVehicle') }}
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import 'maplibre-gl/dist/maplibre-gl.css'
import {defineComponent, markRaw} from 'vue'
import maplibregl from 'maplibre-gl'
import {
  getLastVehiclesPos,
  getParameterFromToken,
  getReverseGeocode,
  getSentGeoActive,
  getVehicleDetails
} from '@/crud/getters'
import {getHumanDateTime} from '@/utils/tools'
import constants from '@/utils/constants'
import moment from 'moment'
import {vehiclesFromMapStore} from '@/stores/vehiclesFromMapStore'
import {createMapStyle, DEFAULT_CENTER, MapProvider, resolveProvider, transformMapRequest} from '@/services/map/map-style'
import {
  applyVehicleMarkerContent,
  createVehicleMarkerElement
} from '@/services/map/vehicle-marker'
import {VehicleClusterManager} from '@/services/map/vehicle-cluster'
import {detectStops} from '@/services/map/route-events'
import {buildRouteArrowFeatures} from '@/services/map/route-arrows'
import {
  clearPlannedRouteData,
  ensurePlannedRouteLayer,
  formatRouteDuration,
  planRoute,
  setPlannedRouteData
} from '@/services/map/route-planner'
import {
  boundsFromLngLat,
  emptyFeatureCollection,
  LngLat,
  lineFeatureCollection,
  pointsToLngLat,
  pointToLngLat,
  tuplesToLngLat,
  tupleToLngLat
} from '@/services/map/route-geometry'

const SRC_FULL = 'route-full'
const SRC_ANIMATION = 'route-animation'
const SRC_POINTS = 'route-points'
const SRC_ARROWS = 'route-arrows'

export default defineComponent({
  name: 'Map',
  data() {
    return {
      refreshInterval: null,
      isFirstLoad: true,
      zoom: 8,
      minZoom: 3,
      maxZoom: 18,
      map: null as maplibregl.Map | null,
      clusterManager: null as VehicleClusterManager | null,
      currentProvider: null as MapProvider | null,
      historyAnimation: {
        marker: null as maplibregl.Marker | null,
        animationMarker: null as ReturnType<typeof setInterval> | null,
        animationPos: 0,
        coords: [] as LngLat[],
        routeLngLat: [] as LngLat[]
      },
      markers: [] as Array<maplibregl.Marker>,
      markerVehicles: [] as Array<any>,
      pointMarkers: [] as Array<maplibregl.Marker>,
      endpointMarkers: [] as Array<maplibregl.Marker>,
      statePopup: null as maplibregl.Popup | null,
      vehicleDetails: {
        drive_time: 0 as number,
        idle_time: 0 as number,
        standing_time: 0 as number,
        average_speed: 0 as number,
        route: [] as Array<{
          basic_speed: number,
          ignition: number,
          device_timestamp: number,
        }>
      },
      sentGeoImei: [] as Array<number>,
      selectedVehicleImei: null,
      allMarkersVisible: true,
      isLoadingVehicleDetails: false,
      fullRouteVisible: false,
      routePointsVisible: false,
      routePlanner: {
        active: false,
        loading: false,
        summary: null as null | {distance: number, duration: number},
        destinationMarker: null as maplibregl.Marker | null
      }
    }
  },
  props: {
    showIcons: { type: Boolean, default: true },
    route: { type: Array },
    // Road-snapped [lat, lng] polyline of the route (OSRM map matching); null = draw raw GPS line.
    matchedRoute: { type: Array, default: null },
    additionalRouteParameters: { type: Array },
    clickState: { type: Number, default: constants.NOT_RUNNING_YET },
    animationDelay: { type: Number, default: 2000 },
    propAnimationPos: { type: Number, default: 0 },
    locked: {type: Boolean, default: true},
    disableLastVehiclesPos: {type: Boolean, default: false}
  },
  methods: {
    async createMap(provider: MapProvider) {
      const self: any = this
      self.currentProvider = provider

      const style = await createMapStyle(provider)

      // markRaw keeps the heavy MapLibre instance out of Vue's reactivity proxy.
      self.map = markRaw(new maplibregl.Map({
        container: 'map',
        style,
        center: DEFAULT_CENTER,
        zoom: self.zoom,
        minZoom: self.minZoom,
        maxZoom: self.maxZoom,
        // Reroute not-hosted Poppins glyph ranges (Greek/Cyrillic/…) to OpenFreeMap Noto Sans,
        // so foreign base-map labels stop 404-flooding the console.
        transformRequest: transformMapRequest,
        // Default top-down (pitch 0); the 3D building layer is in the style, so users still see buildings
        // stand up when they tilt the map manually (right-drag / NavigationControl).
        // Required so History can rasterize the WebGL canvas into a PNG (MapLibre v5 option).
        canvasContextAttributes: {preserveDrawingBuffer: true}
      }))

      // History keeps MapLibre's zoom control; the live fleet map uses the custom bottom-right
      // controls cluster (MapView) instead.
      if (!self.showIcons) {
        self.map.addControl(new maplibregl.NavigationControl(), 'top-left')
      }

      // Dragging while the route animation auto-pans should release the lock.
      self.map.on('mousedown', () => {
        if (self.clickState === constants.RUNNING && self.locked) {
          self.$emit('updateLocked', false)
        }
      })

      self.map.on('load', () => {
        self.onMapLoaded()
      })
    },
    /**
     * Switch the base-map provider at runtime (live fleet map controls). Rebuilds the map via
     * createMap so clustering/markers/route layers are re-initialised cleanly; the next
     * addVehiclesToMap re-fetches and re-adds markers. No-op when unchanged.
     */
    async changeProvider(provider: MapProvider) {
      const self: any = this
      if (!self.map || self.currentProvider === provider) return
      try {
        self.map.remove()
      } catch (_) {
        // ignore teardown errors
      }
      self.markers = []
      self.markerVehicles = []
      self.clusterManager = null
      await self.createMap(provider)
    },
    /**
     * Draw a vehicle's travelled track for an arbitrary range (fed by the info panel's already
     * fetched route_data): base polyline, per-point circles with the shared hover tooltip
     * (time + speed), and stop markers with rich tooltips. Reuses the existing route layers and
     * marker/tooltip primitives; unlike getVehicleDetails it is not limited to the last 24 h.
     */
    renderVehicleTrack(points: Array<any>) {
      const self: any = this
      if (!self.map) return

      const valid = (Array.isArray(points) ? points : []).filter(
        (p: any) => p && !isNaN(Number(p.lat)) && !isNaN(Number(p.lng))
      )
      if (!valid.length) {
        self.hideDetails()
        return
      }

      // Focus mode: pause the live fleet refresh while a single track is shown.
      self.isLoadingVehicleDetails = true

      const lngLat: LngLat[] = valid.map((p: any) => [Number(p.lng), Number(p.lat)] as LngLat)
      self.setFullRouteData(lngLat)
      self.fullRouteVisible = true

      // Legacy mapv3 look: no per-point dots — the direction arrows are the track points. Each
      // carries the travel bearing (rotation) plus time+speed for the hover popup, and MapLibre
      // declutters them by zoom (icon-allow-overlap:false).
      self.setRoutePointsData(emptyFeatureCollection())
      self.setRouteArrowsData(buildRouteArrowFeatures(valid))

      // Stop markers (postoje) with rich tooltips — shared stop detection with the panel.
      self.pointMarkers.forEach((m: any) => m?.remove())
      self.pointMarkers = []
      detectStops(valid).forEach((s) => {
        const start = moment.unix(s.startTs)
        const stop = moment.unix(s.endTs)
        const iconHtml = '<img class="veh-icon" style="width:24px;height:24px;" src="img/map/icons/stop.svg" alt="Stop" />'
        const tooltip = `Start: ${start.format('YYYY-MM-DD HH:mm')}<br>`
          + `Stop: ${stop.format('YYYY-MM-DD HH:mm')}<br>`
          + `${self.$t('components.map.breakDuration')}: ${stop.diff(start, 'minutes')} min`
        self.pointMarkers.push(self.buildStateMarker([s.lng, s.lat] as LngLat, iconHtml, tooltip, true))
      })

      // Start / end medallions (legacy mapv3-track-endpoint): a green flag on the first point and
      // a dark finish flag on the last (offset down so it clears the vehicle pin bubble).
      self.endpointMarkers.forEach((m: any) => m?.remove())
      self.endpointMarkers = []
      const first = valid[0]
      const last = valid[valid.length - 1]
      self.endpointMarkers.push(
        self.buildEndpointMarker([Number(first.lng), Number(first.lat)] as LngLat, 'start', 'Start', [0, 0])
      )
      if (valid.length > 1) {
        self.endpointMarkers.push(
          self.buildEndpointMarker([Number(last.lng), Number(last.lat)] as LngLat, 'end', 'Koniec', [0, 16])
        )
      }
      // No auto-fit here: selecting a vehicle centres the camera on its current point
      // (MapView → centerOn); fitting the whole track would fight that.
    },
    /** Zoom controls for the custom cluster (live fleet map). */
    zoomIn() {
      (this as any).map?.zoomIn()
    },
    zoomOut() {
      (this as any).map?.zoomOut()
    },
    /** Pan the camera so a single point sits in the centre of the map (keeps the current zoom). */
    centerOn(lngLat: [number, number]) {
      const self: any = this
      if (!self.map || !Array.isArray(lngLat)) return
      const lng = Number(lngLat[0])
      const lat = Number(lngLat[1])
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) return
      self.map.panTo([lng, lat], {duration: 700})
    },
    /** Re-fit the camera to all currently visible vehicles (fit-fleet control). */
    fitFleet() {
      const self: any = this
      if (!self.map) return
      const coords: Array<[number, number]> = (self.markerVehicles || [])
        .filter((v: any) => v && v.visible && !isNaN(Number(v.lat)) && !isNaN(Number(v.lng)))
        .map((v: any) => [Number(v.lng), Number(v.lat)] as [number, number])
      if (!coords.length) return
      const bounds = coords.reduce(
        (b: maplibregl.LngLatBounds, c: [number, number]) => b.extend(c),
        new maplibregl.LngLatBounds(coords[0], coords[0])
      )
      self.map.fitBounds(bounds, {padding: 60, maxZoom: self.maxZoom})
    },
    onMapLoaded() {
      const self: any = this
      this.initRouteLayers()

      if (this.showIcons) {
        const map: any = this.map

        // Cluster only the live fleet map; History reuses this component with
        // show-icons=false / disable-last-vehicles-pos and must stay unaffected.
        if (!this.disableLastVehiclesPos) {
          const manager = markRaw(new VehicleClusterManager(map, {
            showVehicle: (index: number, lngLat?: LngLat) => self.showVehicleMarker(index, lngLat),
            hideVehicle: (index: number) => self.hideVehicleMarker(index),
            restoreVehicle: (index: number) => self.restoreVehicleMarker(index)
          }))
          manager.init()
          this.clusterManager = manager
        }

        this.addVehiclesToMap()
        ensurePlannedRouteLayer(map)
        map.on('click', (e: any) => this.onMapClickPlanRoute(e))
      }

      if (this.route?.length) {
        this.addPolylines()
      }
    },
    toggleRoutePlanner() {
      this.routePlanner.active = !this.routePlanner.active
      if (!this.routePlanner.active) {
        this.clearRoutePlan()
      }
    },
    onMapClickPlanRoute(e: any) {
      const self: any = this
      if (!self.routePlanner.active || self.routePlanner.loading) return

      const vehicle = self.markerVehicles.find((v: any) => v?.imei === self.selectedVehicleImei)
      if (!vehicle) return

      self.planRouteForVehicle(vehicle, {lat: e.lngLat.lat, lng: e.lngLat.lng})
    },
    async planRouteForVehicle(vehicle: any, destination: {lat: number, lng: number}) {
      const self: any = this
      self.routePlanner.loading = true
      self.setDestinationMarker(destination)

      try {
        const route = await planRoute([
          {lat: Number(vehicle.lat), lng: Number(vehicle.lng)},
          destination
        ])
        setPlannedRouteData(self.map, route)
        self.routePlanner.summary = {distance: route.distance, duration: route.duration}

        const bounds = boundsFromLngLat(route.geometry.coordinates as LngLat[])
        if (bounds) self.map.fitBounds(bounds, {padding: 60, maxZoom: self.maxZoom})
      } catch (error: any) {
        self.clearRoutePlan()
        const message = error?.response?.status === 422
            ? self.$t('components.map.routePlanning.noRoute')
            : self.$t('components.map.routePlanning.error')
        self.$toast.open({message, type: 'warning', position: 'top-right'})
      } finally {
        self.routePlanner.loading = false
      }
    },
    setDestinationMarker(destination: {lat: number, lng: number}) {
      const self: any = this
      const lngLat: LngLat = [destination.lng, destination.lat]
      if (self.routePlanner.destinationMarker) {
        self.routePlanner.destinationMarker.setLngLat(lngLat)
      } else {
        self.routePlanner.destinationMarker = markRaw(
            new maplibregl.Marker({color: '#1a73e8'}).setLngLat(lngLat).addTo(self.map)
        )
      }
    },
    clearRoutePlan() {
      const self: any = this
      if (self.map) clearPlannedRouteData(self.map)
      self.routePlanner.destinationMarker?.remove()
      self.routePlanner.destinationMarker = null
      self.routePlanner.summary = null
    },
    formatRouteDuration,
    initRouteLayers() {
      const map = this.map
      if (!map) return

      if (!map.getSource(SRC_FULL)) {
        map.addSource(SRC_FULL, {type: 'geojson', data: emptyFeatureCollection() as any})
        // Drawn track colour = app primary, read at runtime so client-branding overrides apply
        // (MapLibre paint needs a concrete colour, not a CSS var).
        const trackColor = getComputedStyle(document.documentElement)
          .getPropertyValue('--primary-color').trim() || '#f77f01'
        // Legacy mapv3 track look: a soft wide casing under a solid line.
        map.addLayer({
          id: `${SRC_FULL}-casing`,
          type: 'line',
          source: SRC_FULL,
          layout: {'line-join': 'round', 'line-cap': 'round'},
          paint: {'line-color': trackColor, 'line-width': 10, 'line-opacity': 0.2}
        })
        map.addLayer({
          id: SRC_FULL,
          type: 'line',
          source: SRC_FULL,
          layout: {'line-join': 'round', 'line-cap': 'round'},
          paint: {'line-color': trackColor, 'line-width': 4.5, 'line-opacity': 0.92}
        })
      }

      if (!map.getSource(SRC_ANIMATION)) {
        map.addSource(SRC_ANIMATION, {type: 'geojson', data: emptyFeatureCollection() as any})
        map.addLayer({
          id: `${SRC_ANIMATION}-outline`,
          type: 'line',
          source: SRC_ANIMATION,
          layout: {'line-join': 'round', 'line-cap': 'round'},
          paint: {'line-color': '#f00', 'line-width': 5}
        })
        map.addLayer({
          id: `${SRC_ANIMATION}-main`,
          type: 'line',
          source: SRC_ANIMATION,
          layout: {'line-join': 'round', 'line-cap': 'round'},
          paint: {'line-color': '#252422', 'line-width': 3}
        })
      }

      if (!map.getSource(SRC_POINTS)) {
        map.addSource(SRC_POINTS, {type: 'geojson', data: emptyFeatureCollection() as any})
        map.addLayer({
          id: SRC_POINTS,
          type: 'circle',
          source: SRC_POINTS,
          paint: {'circle-radius': 3, 'circle-color': '#f00'}
        })
        this.bindRouteHoverPopup(SRC_POINTS)
      }

      if (!map.getSource(SRC_ARROWS)) {
        this.ensureRouteArrowImage(map)
        map.addSource(SRC_ARROWS, {type: 'geojson', data: emptyFeatureCollection() as any})
        // Legacy mapv3-track-arrows: chevrons rotated to the travel bearing; MapLibre declutters
        // them by zoom (icon-allow-overlap:false), so more appear as you zoom in.
        map.addLayer({
          id: SRC_ARROWS,
          type: 'symbol',
          source: SRC_ARROWS,
          minzoom: 5,
          layout: {
            'icon-image': 'route-arrow',
            'icon-rotate': ['get', 'bearing'],
            'icon-rotation-alignment': 'map',
            'icon-allow-overlap': false,
            'icon-ignore-placement': false,
            'icon-padding': 2,
            'icon-size': ['interpolate', ['linear'], ['zoom'], 6, 0.55, 10, 0.8, 13, 1.0, 16, 1.15]
          }
        })
        this.bindRouteHoverPopup(SRC_ARROWS)
      }
    },
    /**
     * Register the 'route-arrow' symbol image once (legacy mapv3): a white chevron with a dark
     * halo. A solid drive-green arrow would blend into the green track line, so the chevron is
     * white edged by a dark stroke for contrast. Points up (north at icon-rotate 0); the symbol
     * layer rotates it per feature by its `bearing`.
     */
    ensureRouteArrowImage(map: any) {
      if (!map || map.hasImage('route-arrow')) return
      const ratio = 2
      const size = 24
      const canvas = document.createElement('canvas')
      canvas.width = size * ratio
      canvas.height = size * ratio
      const ctx = canvas.getContext('2d')
      if (!ctx) return
      ctx.scale(ratio, ratio)
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      const cx = size / 2
      const chevron = () => {
        ctx.beginPath()
        ctx.moveTo(cx - 5.5, cx + 3.5)
        ctx.lineTo(cx, cx - 5)
        ctx.lineTo(cx + 5.5, cx + 3.5)
      }
      ctx.strokeStyle = 'rgba(15, 23, 42, 0.55)' // dark halo
      ctx.lineWidth = 6.5
      chevron()
      ctx.stroke()
      ctx.strokeStyle = '#ffffff' // white chevron on top
      ctx.lineWidth = 3.2
      chevron()
      ctx.stroke()
      map.addImage('route-arrow', ctx.getImageData(0, 0, size * ratio, size * ratio), {pixelRatio: ratio})
    },
    // Shared route hover popup (time + speed) — bound to both the History point circles and the
    // fleet-track arrows, which carry the same `ts`/`speed` properties.
    bindRouteHoverPopup(sourceId: string) {
      const self: any = this
      const map = self.map
      if (!map) return

      const popup = new maplibregl.Popup({closeButton: false, closeOnClick: false, offset: 6})

      map.on('mouseenter', sourceId, () => {
        map.getCanvas().style.cursor = 'pointer'
      })
      map.on('mouseleave', sourceId, () => {
        map.getCanvas().style.cursor = ''
        popup.remove()
      })
      map.on('mousemove', sourceId, (e: any) => {
        const feature = e.features?.[0]
        if (!feature) return

        const props = feature.properties || {}
        const html = `<i class="bi bi-clock-fill"></i> ${getHumanDateTime(props.ts)}
          <br>
          <i class="bi bi-speedometer"></i> ${props.speed} km/h`
        popup.setLngLat(feature.geometry.coordinates).setHTML(html).addTo(map)
      })
    },
    getGeoJSONSource(id: string): maplibregl.GeoJSONSource | undefined {
      const map: any = this.map
      // map.style is undefined before load and after remove() — guard against both.
      if (!map || !map.style) return undefined
      return map.getSource(id) as maplibregl.GeoJSONSource | undefined
    },
    setAnimationData() {
      this.getGeoJSONSource(SRC_ANIMATION)?.setData(
        lineFeatureCollection(this.historyAnimation.coords) as any
      )
    },
    setFullRouteData(coords: LngLat[]) {
      const data = coords.length ? lineFeatureCollection(coords) : emptyFeatureCollection()
      this.getGeoJSONSource(SRC_FULL)?.setData(data as any)
    },
    setRoutePointsData(featureCollection: any) {
      this.getGeoJSONSource(SRC_POINTS)?.setData(featureCollection)
    },
    setRouteArrowsData(featureCollection: any) {
      this.getGeoJSONSource(SRC_ARROWS)?.setData(featureCollection)
    },
    addVehiclesToMap() {
      const self: any = this
      const t = (key: string) => self.$t(key)
      const validBounds: Array<LngLat> = []
      const points: Array<{index: number, lngLat: LngLat}> = []

      if (this.disableLastVehiclesPos || this.isLoadingVehicleDetails || !this.map) {
        return
      }

      getSentGeoActive()
          .then((response: any) => {
            this.sentGeoImei = Array.isArray(response.data) ? response.data : []
          })

      getLastVehiclesPos()
          .then((response) => {
            const data = Array.isArray(response.data) ? response.data : []
            vehiclesFromMapStore.vehiclesFromMap = data
            self.emitter.emit('vehiclesFromMap', data)

            data.forEach((obj, index) => {
              if (!(obj.visible && !isNaN(obj.lat) && !isNaN(obj.lng))) {
                return
              }

              const ctx = {sentGeoImei: self.sentGeoImei, t}
              const lngLat: LngLat = [Number(obj.lng), Number(obj.lat)]
              self.markerVehicles[index] = obj

              if (self.markers[index] === undefined) {
                const element = createVehicleMarkerElement(obj, ctx)
                const marker = markRaw(new maplibregl.Marker({element, anchor: 'bottom'}).setLngLat(lngLat))
                self.markers[index] = marker
                self.wireVehicleMarker(element, index)
              } else {
                const marker = self.markers[index]
                applyVehicleMarkerContent(marker.getElement(), obj, ctx)
                marker.setLngLat(lngLat)
              }

              points.push({index, lngLat})
              validBounds.push(lngLat)
            })

            self.applyVehicleVisibility(points)

            if (self.isFirstLoad && validBounds.length > 0) {
              const bounds = boundsFromLngLat(validBounds)
              if (bounds) {
                self.map.fitBounds(bounds, {padding: 48, maxZoom: self.maxZoom})
              }
              self.isFirstLoad = false
            }
          })
    },
    // Visibility is decided in one place: the cluster manager owns it when all vehicles
    // are shown, while selected mode keeps only the picked vehicle on the map.
    applyVehicleVisibility(points: Array<{index: number, lngLat: LngLat}>) {
      const self: any = this

      if (this.clusterManager) {
        this.clusterManager.update(points)
      }

      if (self.allMarkersVisible) {
        if (!this.clusterManager) {
          points.forEach(({index}) => self.markers[index]?.addTo(self.map))
        }
        return
      }

      points.forEach(({index}) => {
        const marker = self.markers[index]
        if (!marker) return
        const isSelected = self.markerVehicles[index]?.imei === self.selectedVehicleImei
        const isOnMap = !!marker.getElement().parentNode
        if (isSelected && !isOnMap) marker.addTo(self.map)
        else if (!isSelected && isOnMap) marker.remove()
      })
    },
    showVehicleMarker(index: number, lngLat?: LngLat) {
      const self: any = this
      const marker = self.markers[index]
      if (!marker) return
      if (lngLat) marker.setLngLat(lngLat)
      if (!marker.getElement().parentNode) marker.addTo(self.map)
    },
    hideVehicleMarker(index: number) {
      const self: any = this
      const marker = self.markers[index]
      if (marker && marker.getElement().parentNode) marker.remove()
    },
    restoreVehicleMarker(index: number) {
      const self: any = this
      const marker = self.markers[index]
      const obj = self.markerVehicles[index]
      if (marker && obj) marker.setLngLat([Number(obj.lng), Number(obj.lat)])
    },
    wireVehicleMarker(element: HTMLElement, index: number) {
      const self: any = this

      // Hover tooltip is pure CSS (.veh-pin__tip); only the click needs JS.
      element.addEventListener('click', (event) => {
        event.stopPropagation()
        const obj = self.markerVehicles[index]
        if (!obj) return

        self.hideOtherMarkers(obj.imei)
        if (self.showIcons) {
          // Live fleet map: open the mapv3 info panel (MapView listens). History mode
          // (show-icons=false) keeps the classic summary sidebar and is unaffected.
          self.emitter.emit('mapVehicleSelected', obj)
        } else {
          window.openSidebarWithComponent({level: 0, componentName: 'HistorySummary', title: obj.registration, props: obj})
        }
      })
    },
    addPolylines() {
      const self: any = this
      if (!this.map || !this.route?.length) return

      const routeLngLat = tuplesToLngLat(this.route as any)
      self.historyAnimation.routeLngLat = routeLngLat
      self.historyAnimation.coords = [routeLngLat[0]]
      self.setAnimationData()

      // Settle the camera on the whole route in one instant move - any further
      // fitBounds calls (SHOW_FULL_ROUTE, refreshMapBounds) target the same frame,
      // so the map no longer hops between the start point and the route extent.
      const bounds = boundsFromLngLat(routeLngLat)
      if (bounds) self.map.fitBounds(bounds, {padding: 30, maxZoom: self.maxZoom, duration: 0})

      const element = document.createElement('img')
      element.src = 'img/map/icons/pin.png'
      element.className = 'route-pin-marker'
      self.historyAnimation.marker = markRaw(new maplibregl.Marker({element, anchor: 'bottom'})
          .setLngLat(routeLngLat[0])
          .addTo(self.map))

      this.$emit('showControls', true)
      this.$emit('emitAnimationPos', self.historyAnimation.animationPos)

      self.controlInterval()
    },
    controlInterval() {
      const self: any = this
      const ha = self.historyAnimation
      const route: Array<any> = (self.route as Array<any>) || []
      const routeLngLat: LngLat[] = ha.routeLngLat

      if (this.clickState === constants.STOPPED) {
        if (ha.animationMarker) clearInterval(ha.animationMarker)
        ha.coords = [routeLngLat[0]]
        self.setAnimationData()
        ha.marker?.setLngLat(routeLngLat[0])
        ha.animationPos = 0
        this.$emit('emitAnimationPos', ha.animationPos)
      } else if (this.clickState === constants.RUNNING) {
        ha.animationMarker = setInterval(() => {
          if (ha.animationPos === route.length) {
            self.$emit('updateClickState', constants.STOPPED)
            ha.animationPos = 0
            return
          }

          // Follow mode: re-center only when the pin leaves the viewport,
          // otherwise the camera would hop on every animation tick.
          if (self.locked && !self.map.getBounds().contains(routeLngLat[ha.animationPos])) {
            self.map.panTo(routeLngLat[ha.animationPos], {duration: 600})
          }

          this.$emit('emitAnimationPos', ha.animationPos)
          ha.coords.push(routeLngLat[ha.animationPos])
          self.setAnimationData()
          ha.marker?.setLngLat(routeLngLat[ha.animationPos])
          ha.animationPos++
        }, self.animationDelay)
      } else if (this.clickState === constants.PAUSED) {
        if (ha.animationMarker) clearInterval(ha.animationMarker)
      } else if (this.clickState === constants.SHOW_FULL_ROUTE) {
        if (self.fullRouteVisible) {
          self.setFullRouteData([])
          self.fullRouteVisible = false
          self.$emit('updateClickState', constants.HIDE_FULL_ROUTE)
        } else {
          // Road-snapped geometry when available; raw GPS polyline otherwise.
          const fullLine: LngLat[] = self.matchedRoute?.length
              ? self.matchedRoute.map(([lat, lng]: [number, number]) => [lng, lat] as LngLat)
              : routeLngLat
          self.setFullRouteData(fullLine)
          self.fullRouteVisible = true
          const bounds = boundsFromLngLat(routeLngLat)
          if (bounds) self.map.fitBounds(bounds, {padding: 30, maxZoom: self.maxZoom})
        }
        if (ha.animationMarker) clearInterval(ha.animationMarker)
      } else if (this.clickState === constants.HIDE_FULL_ROUTE) {
        if (ha.animationMarker) clearInterval(ha.animationMarker)
        self.setFullRouteData([])
        self.fullRouteVisible = false
      } else if (this.clickState === constants.SHOW_ROUTE_POINTS) {
        if (self.routePointsVisible) {
          self.setRoutePointsData(emptyFeatureCollection())
          self.routePointsVisible = false
          self.$emit('updateClickState', constants.HIDE_ROUTE_POINTS)
        } else {
          self.setRoutePointsData(self.buildRoutePointFeatures())
          self.routePointsVisible = true
        }
        if (ha.animationMarker) clearInterval(ha.animationMarker)
      } else if (this.clickState === constants.HIDE_ROUTE_POINTS) {
        if (ha.animationMarker) clearInterval(ha.animationMarker)
        self.setRoutePointsData(emptyFeatureCollection())
        self.routePointsVisible = false
      }
    },
    buildRoutePointFeatures() {
      const self: any = this
      const params: Array<any> = (self.additionalRouteParameters as Array<any>) || []
      const route: Array<any> = (self.route as Array<any>) || []

      return {
        type: 'FeatureCollection',
        features: route.map((point, index) => ({
          type: 'Feature',
          properties: {
            ts: params[index]?.device_timestamp,
            speed: params[index]?.basic_speed
          },
          geometry: {type: 'Point', coordinates: tupleToLngLat(point)}
        }))
      }
    },
    ensureStatePopup() {
      const self: any = this
      if (!self.statePopup) {
        self.statePopup = markRaw(new maplibregl.Popup({closeButton: false, closeOnClick: false, offset: 12, className: 'state-tooltip'}))
      }
      return self.statePopup
    },
    buildStateMarker(coord: LngLat, iconHtml: string, tooltipHtml: string, isTop: boolean): maplibregl.Marker {
      const self: any = this
      const element = document.createElement('div')
      element.className = 'veh-icon-container'
      element.innerHTML = iconHtml
      if (isTop) element.style.zIndex = '5000'

      const marker = markRaw(new maplibregl.Marker({element, anchor: 'center'}).setLngLat(coord).addTo(self.map))

      element.addEventListener('mouseenter', () => {
        self.ensureStatePopup().setLngLat(coord).setHTML(tooltipHtml).addTo(self.map)
      })
      element.addEventListener('mouseleave', () => {
        self.statePopup?.remove()
      })

      return marker
    },
    /** Route start/end medallion (legacy mapv3-track-endpoint): a bordered flag medal + label pill. */
    buildEndpointMarker(coord: LngLat, kind: 'start' | 'end', label: string, offset: [number, number]): maplibregl.Marker {
      const self: any = this
      const flagStart = '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 21V4M5 4h11l-2.5 3.5L16 11H5"/></svg>'
      const flagFinish = '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 21V4"/><rect x="5" y="4" width="12" height="9" rx="0.5"/><path d="M5 8.5h12M11 4v9M5 4h6v4.5h6"/></svg>'
      const element = document.createElement('div')
      element.className = `veh-endpoint veh-endpoint--${kind}`
      element.setAttribute('title', label)
      element.innerHTML =
        `<span class="veh-endpoint__medal">${kind === 'start' ? flagStart : flagFinish}</span>` +
        `<span class="veh-endpoint__label">${label}</span>`
      return markRaw(new maplibregl.Marker({element, anchor: 'center', offset}).setLngLat(coord).addTo(self.map))
    },
    getVehicleDetails(imei: bigint, segmentStart?: number, segmentStop?: number) {
      const self: any = this
      this.isLoadingVehicleDetails = true

      // Backwards-compat with the old API: collapse segmentStart/segmentStop into segmentHours.
      let segmentHours: number | undefined = undefined
      if (segmentStart !== undefined && segmentStop !== undefined) {
        segmentHours = segmentStop
      } else if (segmentStart !== undefined) {
        segmentHours = segmentStart
      }

      getVehicleDetails(imei, segmentHours, segmentStart, segmentStop)
          .then((res) => {
            if (res.status === 204 || !res.data || !res.data.route || res.data.route.length === 0) {
              self.$swal.fire({
                icon: 'info',
                text: self.$t('components.noRouteFound'),
                confirmButtonText: 'OK'
              }).then(() => {
                self.showAllMarkers()
              })
              window.clearSidebars()
              self.hideDetails()
              self.isLoadingVehicleDetails = false
              return
            }

            this.vehicleDetails = res.data
            this.isLoadingVehicleDetails = false

            if (self.vehicleDetails.place_start)
              getReverseGeocode(self.vehicleDetails.place_start)
                  .then(res => {
                    self.vehicleDetails.place_start = res.data
                  })

            self.hideDetails()

            // Prefer the road-snapped geometry from the backend (OSRM map matching);
            // fall back to the raw GPS polyline when matching was not possible.
            const matched: Array<[number, number]> | null = res.data.matched_route
            const routeLngLat = matched?.length
                ? matched.map(([lat, lng]) => [lng, lat] as LngLat)
                : pointsToLngLat(res.data.route)
            self.setFullRouteData(routeLngLat)
            self.fullRouteVisible = true
            self.fitPolylineToLeftHalf(routeLngLat)

            if (self.pointMarkers.length > 0)
              self.pointMarkers = []

            let iconText = ''

            res.data.route.forEach((obj, index, array) => {
              if (index + 1 !== array.length) { // not display last one
                const parts: Array<any> = []
                let isTopMarker = false
                let iconHtml = ''

                if (obj.state_string == 'driving') {
                  if (obj.basic_speed !== null && obj.basic_speed >= 0) parts.push(`${ obj.basic_speed } [km/h]`)
                  if (obj.fuel_level !== null && obj.fuel_level >= 0) parts.push(`${ obj.fuel_level } [%]`)
                  if (obj.can_odometer !== null && obj.can_odometer >= 0) parts.push(`${ obj.can_odometer } [km]`)

                  iconText = `${ getHumanDateTime(obj.device_timestamp) } | ${ parts.join(' | ') }`
                  iconHtml = `<img class="veh-icon" style="width: 16px; height: 16px; transform: rotate(${ obj.heading }deg)" src="img/map/icons/0.png" alt="Vehicle" />`

                } else if (obj.state_string == 'break') {
                  // Show one marker per break: only at its last point, where break_stop is known.
                  if (array[index + 1] == undefined || array[index + 1].state_string == 'break') return

                  if (obj?.break_stop) {
                    const breakStart = moment.unix(obj.device_timestamp)
                    const breakStop = moment.unix(obj.break_stop)

                    parts.push(`Start: ${ breakStart.format('YYYY-MM-DD HH:mm:ss') }`)
                    parts.push(`Stop: ${ breakStop.format('YYYY-MM-DD HH:mm:ss') }`)
                    parts.push(`${ self.$t('components.map.breakDuration') }: ${ breakStop.diff(breakStart, 'minutes') } min`)
                    if (obj.can_odometer !== null && obj.can_odometer >= 0) {
                      parts.push(`${ self.$t('components.history.dailySummary.dataGrids.odometer.odometer') }: ${ obj.can_odometer }km`)
                    }
                  }

                  iconHtml = `<img class="veh-icon" style="width: 24px; height: 24px;" src="img/map/icons/stop.svg"  alt="Vehicle" />`
                  iconText = `${ parts.join('<br>') }`
                  isTopMarker = true
                } else {
                  iconHtml = `<img class="veh-icon" style="width: 16px; height: 16px; transform: rotate(${ obj.heading }deg)" src="img/map/icons/0.png" alt="Vehicle" />`
                }

                self.pointMarkers[index] = self.buildStateMarker(pointToLngLat(obj), iconHtml, iconText, isTopMarker)
              }
            })
          })
          .catch(() => {
            self.$swal.fire({
              icon: 'warning',
              title: 'Oops...',
              text: self.$t('components.error')
            }).then(() => {
              self.showAllMarkers()
            })
            window.clearSidebars()
            self.hideDetails()
            self.isLoadingVehicleDetails = false
          })

      return this.vehicleDetails
    },
    hideDetails(): void {
      const self: any = this

      self.setFullRouteData([])
      self.setRoutePointsData(emptyFeatureCollection())
      self.setRouteArrowsData(emptyFeatureCollection())
      self.fullRouteVisible = false

      self.pointMarkers.forEach((marker) => marker?.remove())
      self.pointMarkers = []
      self.endpointMarkers.forEach((marker: any) => marker?.remove())
      self.endpointMarkers = []
      self.statePopup?.remove()
    },
    fitPolylineToLeftHalf(routeLngLat: LngLat[]) {
      const bounds = boundsFromLngLat(routeLngLat)
      if (!bounds || !this.map) return

      const width = this.map.getContainer().clientWidth
      this.map.fitBounds(bounds, {
        padding: {top: 20, right: 20, bottom: 20, left: Math.max(20, Math.floor(width * 0.5))},
        maxZoom: this.maxZoom,
        duration: 0
      })
    },
    // Public API used by History.vue (route is [lat, lng] tuples).
    fitBounds(route: Array<[number, number]>) {
      if (!this.map || !route?.length) return
      const bounds = boundsFromLngLat(tuplesToLngLat(route))
      if (bounds) this.map.fitBounds(bounds, {padding: 30, maxZoom: this.maxZoom, duration: 0})
    },
    invalidateSize() {
      this.map?.resize()
    },
    hideOtherMarkers(selectedImei) {
      const self: any = this
      self.selectedVehicleImei = selectedImei
      self.allMarkersVisible = false
      this.clusterManager?.disable()

      vehiclesFromMapStore.vehiclesFromMap.forEach((vehicle, index) => {
        if (!self.markers[index]) return
        if (vehicle.imei === selectedImei) {
          // The selected vehicle may have been clustered/fanned out — bring it back, visible.
          self.restoreVehicleMarker(index)
          if (!self.markers[index].getElement().parentNode) self.markers[index].addTo(self.map)
        } else {
          self.markers[index].remove()
        }
      })
    },
    showAllMarkers() {
      const self: any = this
      self.selectedVehicleImei = null
      self.allMarkersVisible = true
      self.clearRoutePlan()

      if (this.clusterManager) {
        this.clusterManager.enable()
        return
      }

      vehiclesFromMapStore.vehiclesFromMap.forEach((vehicle, index) => {
        if (self.markers[index] && vehicle.visible && !isNaN(vehicle.lat) && !isNaN(vehicle.lng)) {
          self.markers[index].addTo(self.map)
        }
      })
    }
  },
  watch: {
    vehicleDetails: {
      handler(val) {
        const self: any = this
        self.emitter.emit('vehicleDetailsResponse', val)
      },
      deep: true
    },
    clickState() {
      this.controlInterval()
    },
    animationDelay() {
      if (this.historyAnimation.animationMarker) clearInterval(this.historyAnimation.animationMarker)
      this.controlInterval()
    },
    propAnimationPos(value) {
      const self: any = this
      const ha = self.historyAnimation
      if (ha.animationMarker) clearInterval(ha.animationMarker)

      const part = ha.routeLngLat.slice(0, value)
      ha.coords = part.length ? part : [ha.routeLngLat[0]]
      self.setAnimationData()

      const last = part.length ? part[part.length - 1] : ha.routeLngLat[0]
      ha.marker?.setLngLat(last)
      if (self.locked && self.map && !self.map.getBounds().contains(last)) {
        self.map.panTo(last, {duration: 600})
      }

      ha.animationPos = value
      self.$emit('updateClickState', constants.PAUSED)
    },
    locked(value) {
      const self: any = this
      if (value === true) {
        self.map?.panTo(self.historyAnimation.routeLngLat[self.historyAnimation.animationPos])
      }
    }
  },
  beforeUnmount() {
    const self: any = this

    if (this.refreshInterval) {
      clearInterval(this.refreshInterval)
    }
    if (this.historyAnimation.animationMarker) {
      clearInterval(this.historyAnimation.animationMarker)
    }

    // Detach the global emitter handlers so stale instances (HMR / route changes)
    // never operate on an already-removed map.
    if (self.emitterHandlers) {
      self.emitter.off('getVehicleDetails', self.emitterHandlers.getVehicleDetails)
      self.emitter.off('closeSidebar', self.emitterHandlers.closeSidebar)
      self.emitter.off('hideOtherMarkers', self.emitterHandlers.hideOtherMarkers)
      self.emitter.off('renderVehicleTrack', self.emitterHandlers.renderVehicleTrack)
    }

    this.clusterManager?.destroy()
    self.markers.forEach((marker) => marker?.remove())
    self.pointMarkers.forEach((marker) => marker?.remove())
    self.historyAnimation.marker?.remove()
    self.routePlanner.destinationMarker?.remove()
    self.statePopup?.remove()

    if (self.map) {
      try {
        self.map.remove()
      } catch (e) {
        // ignore cleanup errors
      }
      self.map = null
    }
  },
  mounted() {
    const self: any = this

    getParameterFromToken('MAP_TYPE')
        .then((res: string) => {
          this.createMap(resolveProvider(res))
        })
        .catch(() => {
          // No MAP_TYPE claim in the token -> fall back to the default base map (OpenFreeMap).
          this.createMap('OFM')
        })

    self.emitterHandlers = {
      getVehicleDetails: (data) => {
        if (typeof data === 'object' && data.imei) {
          if (data.segmentStart !== undefined && data.segmentStop !== undefined) {
            self.getVehicleDetails(data.imei, data.segmentStart, data.segmentStop)
          } else if (data.segmentHours !== undefined) {
            self.getVehicleDetails(data.imei, undefined, data.segmentHours)
          } else {
            self.getVehicleDetails(data.imei)
          }
        } else {
          self.getVehicleDetails(data)
        }
      },
      closeSidebar: () => {
        self.hideDetails()
        // Resume the live fleet refresh that renderVehicleTrack/getVehicleDetails paused.
        self.isLoadingVehicleDetails = false
        self.showAllMarkers()
      },
      hideOtherMarkers: (imei) => {
        self.hideOtherMarkers(imei)
      },
      renderVehicleTrack: (points) => {
        self.renderVehicleTrack(points)
      }
    }

    self.emitter.on('getVehicleDetails', self.emitterHandlers.getVehicleDetails)
    self.emitter.on('renderVehicleTrack', self.emitterHandlers.renderVehicleTrack)
    self.emitter.on('closeSidebar', self.emitterHandlers.closeSidebar)
    self.emitter.on('hideOtherMarkers', self.emitterHandlers.hideOtherMarkers)

    if (!this.disableLastVehiclesPos) {
      self.refreshInterval = setInterval(() => {
        self.addVehiclesToMap()
      }, parseInt(process.env.VUE_APP_MAP_INTERVAL_REFRESH))
    }
  }
})
</script>
<style lang="scss" src="@/assets/scss/vehicles-map.scss"></style>
<style lang="scss" src="@/assets/scss/route-planner.scss"></style>
